export const BLOG_POSTS = [
  {
    id: "corporate-catering-trends-2026",
    slug: "corporate-catering-trends-2026",
    title: "Top Corporate Catering Trends in 2026",
    excerpt: "Discover the latest trends shaping corporate dining in Bangalore — from sustainable packaging to AI-powered menu planning.",
    content: `
      <p>The corporate catering landscape in Bangalore is evolving rapidly. As companies prioritize employee wellness and sustainability, catering services are adapting to meet these new expectations.</p>

      <h2>1. Sustainable & Eco-Friendly Packaging</h2>
      <p>Companies are increasingly demanding compostable, plastic-free packaging. Sugarcane fiber boxes, bamboo cutlery, and beeswax wraps are replacing traditional disposables.</p>

      <h2>2. AI-Powered Menu Planning</h2>
      <p>Advanced analytics now help predict employee preferences, reduce food waste by up to 40%, and optimize nutritional balance across menu cycles.</p>

      <h2>3. Hyper-Local Sourcing</h2>
      <p>Farm-to-office is the new standard. Chefs are partnering with local organic farms within 50km of Bangalore to source fresh, seasonal ingredients.</p>

      <h2>4. Dietary Personalization</h2>
      <p>One-size-fits-all menus are out. Employees now expect Jain, vegan, keto, halal, and gluten-free options as standard — not as special requests.</p>

      <h2>5. Live Cooking Stations</h2>
      <p>Interactive food experiences boost employee engagement. Dosa counters, pasta bars, and stir-fry stations are becoming must-haves in tech offices.</p>

      <p>At The Office Meal Co., we stay ahead of these trends to deliver dining experiences that your team actually looks forward to.</p>
    `,
    author: "The Office Meal Co. Team",
    date: "2026-05-15",
    readTime: "5 min read",
    category: "Industry Insights",
    tags: ["trends", "sustainability", "corporate dining"],
    image: "/images/blog/catering-trends-2026.webp",
    featured: true
  },
  {
    id: "how-to-choose-corporate-caterer",
    slug: "how-to-choose-corporate-caterer",
    title: "How to Choose the Right Corporate Caterer in Bangalore",
    excerpt: "A comprehensive checklist for HR and admin teams looking to partner with a corporate catering service.",
    content: `
      <p>Selecting a corporate caterer is one of the most impactful decisions for employee satisfaction. Here's what to look for:</p>

      <h2>1. In-House Kitchen vs. Cloud Kitchen</h2>
      <p>Always choose a caterer with their own certified kitchen. Cloud kitchens and third-party aggregators compromise on quality, hygiene, and consistency.</p>

      <h2>2. Menu Rotation & Variety</h2>
      <p>Ask about their menu cycle. A 28-day rotation with seasonal updates ensures employees never get bored.</p>

      <h2>3. Dietary Accommodation</h2>
      <p>Verify they can handle Jain, vegan, keto, halal, and allergy-specific requirements without cross-contamination.</p>

      <h2>4. On-Site Service Team</h2>
      <p>The best caterers don't just drop food — they serve it. Look for trained captains, stewards, and buffet attendants.</p>

      <h2>5. Hygiene Certifications</h2>
      <p>Check for FSSAI license, ISO 22000, and regular third-party audits. Don't compromise on food safety.</p>

      <p>Download our free <strong>Corporate Caterer Evaluation Checklist</strong> or <a href="/enquire">schedule a tasting</a> with our team.</p>
    `,
    author: "The Office Meal Co. Team",
    date: "2026-05-10",
    readTime: "7 min read",
    category: "Guide",
    tags: ["guide", "bangalore", "corporate catering"],
    image: "/images/blog/choose-caterer.webp",
    featured: false
  },
  {
    id: "jain-vegan-keto-office-meals",
    slug: "jain-vegan-keto-office-meals",
    title: "Jain, Vegan & Keto: How We Handle Dietary Restrictions at Scale",
    excerpt: "Inside our segregated kitchen workflow that ensures every dietary preference is respected — without compromising on taste.",
    content: `
      <p>Feeding 500 employees with 15 different dietary requirements sounds impossible. Here's how we do it every single day.</p>

      <h2>Segregated Kitchen Sections</h2>
      <p>Our 4,000 sq ft kitchen in Electronic City has dedicated zones for Jain, vegan, keto, and regular meal preparation. Each zone has separate utensils, cutting boards, and cooking equipment.</p>

      <h2>Color-Coded System</h2>
      <p>Every container, label, and serving utensil is color-coded. Green for Jain, blue for vegan, red for keto — zero confusion, zero cross-contamination.</p>

      <h2>Chef Specialization</h2>
      <p>Our chefs are trained in specific dietary cuisines. Our Jain chef has 12 years of experience in Jain cooking. Our vegan chef is certified in plant-based nutrition.</p>

      <h2>Employee Preference Tracking</h2>
      <p>Through our digital dashboard, employees can set their dietary preferences once, and we remember them forever. No need to remind us every day.</p>

      <p>Whether your team has 5 dietary requirements or 50, we have the infrastructure and expertise to handle it flawlessly.</p>
    `,
    author: "Chef Rajesh Kumar",
    date: "2026-05-05",
    readTime: "6 min read",
    category: "Behind the Scenes",
    tags: ["dietary", "jain", "vegan", "keto"],
    image: "/images/blog/dietary-meals.webp",
    featured: false
  },
  {
    id: "office-pantry-setup-guide",
    slug: "office-pantry-setup-guide",
    title: "The Complete Guide to Setting Up an Office Pantry in Bangalore",
    excerpt: "Everything you need to know about designing, stocking, and managing a world-class office pantry — from coffee machines to snack curation.",
    content: `
      <p>A well-designed office pantry is more than a break room — it's a productivity and retention tool. Here's our complete guide.</p>

      <h2>Space Planning</h2>
      <p>For every 50 employees, allocate 150 sq ft of pantry space. Include zones for coffee, tea, snacks, fresh fruit, and refrigeration.</p>

      <h2>Equipment Essentials</h2>
      <p>Invest in a commercial espresso machine, cold brew tower, tea dispensers, and a high-capacity refrigerator. Don't forget the microwave and water station.</p>

      <h2>Snack Curation</h2>
      <p>Balance healthy options (nuts, dried fruits, protein bars) with indulgent treats (chocolates, cookies). Label everything with dietary information.</p>

      <h2>SLA-Driven Replenishment</h2>
      <p>Partner with a pantry management service that guarantees 24-hour replenishment. Empty shelves kill morale.</p>

      <h2>Budget Planning</h2>
      <p>Budget ₹150-300 per employee per month for a premium pantry experience. This includes coffee, tea, snacks, and fresh fruit.</p>

      <p>Ready to set up your office pantry? <a href="/enquire">Get a free consultation</a> from our pantry design team.</p>
    `,
    author: "The Office Meal Co. Team",
    date: "2026-04-28",
    readTime: "8 min read",
    category: "Guide",
    tags: ["pantry", "office setup", "bangalore"],
    image: "/images/blog/pantry-setup.webp",
    featured: false
  },
  {
    id: "event-catering-checklist",
    slug: "event-catering-checklist",
    title: "The Ultimate Corporate Event Catering Checklist",
    excerpt: "Planning an award night, conference, or gala dinner? Don't miss these 15 critical checkpoints.",
    content: `
      <p>Corporate events can make or break your company's reputation. Use this checklist to ensure flawless catering.</p>

      <h2>8 Weeks Before</h2>
      <ul>
        <li>Finalize guest count and dietary requirements</li>
        <li>Select caterer and schedule tasting</li>
        <li>Choose menu theme and service style</li>
      </ul>

      <h2>4 Weeks Before</h2>
      <ul>
        <li>Confirm final headcount</li>
        <li>Finalize bar menu and beverage pairings</li>
        <li>Plan table layout and service flow</li>
      </ul>

      <h2>1 Week Before</h2>
      <ul>
        <li>Confirm service staff count and uniforms</li>
        <li>Review contingency plans for dietary emergencies</li>
        <li>Schedule setup and breakdown times</li>
      </ul>

      <h2>Day Of</h2>
      <ul>
        <li>Arrive 3 hours early for setup</li>
        <li>Conduct final hygiene check</li>
        <li>Brief service team on VIP guests and special requests</li>
      </ul>

      <p>Download our complete <strong>Event Catering Playbook</strong> or <a href="/enquire">speak to our events team</a>.</p>
    `,
    author: "The Office Meal Co. Team",
    date: "2026-04-20",
    readTime: "6 min read",
    category: "Checklist",
    tags: ["events", "checklist", "corporate"],
    image: "/images/blog/event-checklist.webp",
    featured: false
  }
];

export const CATEGORIES = [
  "All",
  "Industry Insights",
  "Guide",
  "Behind the Scenes",
  "Checklist"
];
